import {Dialog, DialogContent, Divider, FormLabel, IconButton, ListItem, Typography} from '@mui/material';
import {useTranslation} from 'react-i18next';
import {useDispatch, useSelector} from 'react-redux';
import makeStyles from '@mui/styles/makeStyles';
import React, {useCallback, useEffect, useRef, useState} from 'react';
import {AgyleLogoIcon} from '../../icons/icons';
import clsx from 'clsx';
import uuidv4 from 'uuid/v4';
import AgyleButton from '../../ui/buttons/agyleButton';
import CloseIcon from '@mui/icons-material/Close';
import HistoryIcon from '@mui/icons-material/History';
import List from '@mui/material/List';
import ListItemButton from '@mui/material/ListItemButton';
import DesktopMacIcon from '@mui/icons-material/DesktopMac';
import {
    saveAgylePreference,
    saveCurrentTheme,
    saveSpecialties,
    specialties as specialtiesReducer,
} from '../../../store/reducers/preferences';
import _ from 'lodash';
import ControlSpecialty from '../../../controls/agyle/specialty';
import {setSnackbar} from '../../../store/reducers/globals';
import UserSettingContent from './settingsComponents/userSettingContent';
import DefaultTheme from './settingsComponents/defaultTheme';
import ShowSnomedCode from './settingsComponents/showSnomedCode';
import config from '../../../app/config';
import FilterSettings from './settingsComponents/filterSetting';
import MenuMode from './settingsComponents/menuMode';
import TimestampsDisplay from './settingsComponents/timestampsDisplay';
import SwitchMapMode from './settingsComponents/switchMapMode';
import ClinicalNotesFilterMode from './settingsComponents/clinicalNotesFilterMode';
import SnackbarPosition from './settingsComponents/snackbarPosition';
import useOpenState from '../../../hooks/useOpenState';
import MessageBox from '../../../util/messageBox';
import DisplaySettingsIcon from '@mui/icons-material/DisplaySettings';
import AccessibilityIcon from '@mui/icons-material/Accessibility';
import GroupOutlinedIcon from '@mui/icons-material/GroupOutlined';
import DevicesTab from '../../../pages/centralPage/userPreferences/components/devicesTab';
import SelectedUserSpecialties from './settingsComponents/selectedUserSpecialties';
import DefaultStartingPage from './settingsComponents/defaultStartingPage';
import FormActionBarMode from './settingsComponents/formActionBarMode';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import FavAssessmentsCards from './settingsComponents/favAssessmentsCards';
import {getConfig} from '../../../util/configUtil';
import DefaultSchedulerView from './settingsComponents/defaultSchedulerView';
import {hasPermission} from '../../../store/reducers/user';

export const useSettingsStyles = makeStyles((theme) => ({
    dialogContent: {
        backgroundColor: theme.palette.mode === 'dark' ? '#252525' : '#ebebeb',
        height: '100vh',
        padding: 10,
    },
    divRow: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
    },
    title: {
        // fontWeight: 'bold',
        fontSize: 'large',
    },
    buttons: {
        marginLeft: 'auto',
        gap: 10,
    },
    divider: {
        margin: '10px 0',
    },
    menu: {
        width: '20%',
        borderRight: `1px solid ${theme.palette.mode === 'dark' ? '#404040' : '#cecece'}`,
        height: 'calc(100vh - 80px)',
    },
    menuItem: {
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
        color: 'grey',
        gap: 10,
        minHeight: 40,
    },
    menuItemSelected: {
        borderRadius: 6,
        borderRight: '3px solid #2180F7',
        display: 'flex',
        alignItems: 'center',
        color: theme.palette.bookAdmissions.settingMenuItemSelectedText,
        backgroundColor: theme.palette.bookAdmissions.settingMenuItemSelectedBackground,
    },
    content: {
        paddingLeft: 10,
        width: '80%',
        height: 'calc(100vh - 80px)',
        overflow: 'auto',
    },
    contentBox: {
        borderRadius: 8,
        margin: 10,
        backgroundColor: theme.palette.mode === 'dark' ? '#313233' : '#bfc2c7',
    },
    contentTitle: {
        borderBottom: `2px solid ${theme.palette.mode === 'dark' ? '#252525' : '#ebebeb'}`,
        padding: 10,
    },
    contentChildren: {
        padding: 10,
    },
    specIcon: {
        backgroundColor: theme.palette.mode === 'light' ? '#D2D5D9' : '#3D3E40',
        borderRadius: 6,
        display: 'flex',
        flexWrap: 'nowrap',
        flexDirection: 'row',
        alignItems: 'center',
        justifyContent: 'center',
        gap: 10,
        fontSize: 'small',
        minHeight: 30,
        maxHeight: 30,
        minWidth: 50,
        padding: '5px 15px',
        cursor: 'pointer',
        '&.active': {
            backgroundColor: theme.palette.primary.main,
        },
    },
    activeChip: {
        backgroundColor: theme.palette.primary.main,
    },
    backItem: {
        backgroundColor: theme.palette.background.paperDarker,
        width: '98%',
        padding: 10,
        borderRadius: 6,
        display: 'flex',
        alignItems: 'center',
        gap: 10,
        fontWeight: 'bold',
        height: 60,
        marginTop: 10,
    },
    backIcon: {
        backgroundColor: theme.palette.inPatient.accordionBackground,
    },
    chipContainer: {
        backgroundColor: theme.palette.inPatient.inputBackground,
        padding: '5px 10px',
        borderRadius: 6,
        fontWeight: 'bold',
        display: 'flex',
        alignItems: 'center',
        gap: 5,
    },
    chipIcon: {
      color: theme.palette.primary.main,
    },
}));

// Users Settings
const SYSTEM_PREFERENCES = [
    createEntry('fav-assessments', 'Assessments Cards', <FavAssessmentsCards/>, true),
    createEntry('default-starting-page', 'Default Starting Page', <DefaultStartingPage/>, true),
    createEntry('show-snomed-code', 'SNOMED code', <ShowSnomedCode/>, true),
    createEntry('snackbar-setting', 'Snackbar position', <SnackbarPosition/>, true),
    createEntry('filters-setting', 'Filter mode', <FilterSettings/>, true),
    createEntry('taskbar-mode', 'Top menu mode', <MenuMode/>, true),
    createEntry('timestamps-display-option', 'Timestamps Display Option',
        <TimestampsDisplay/>, config?.timestampsDisplayOption),
    createEntry('map-switch-mode', 'Map Switch Mode', <SwitchMapMode/>, config?.modules?.map?.customMap),
    createEntry('clinical-note-filter', 'Clinical Notes Default Filter',
        <ClinicalNotesFilterMode/>, config?.clinicalNotesDefaultTypeEnable),
    createEntry('form-action-bar', 'Action bar form', <FormActionBarMode/>, true),
    createEntry('default-scheduler-view', 'Default Scheduler View', <DefaultSchedulerView/>, getConfig('modules.scheduler.enabled', false)),
];

export const OPTION_BUTTONS_DEFAULT_GAP = 1.5;

function createEntry(id, name, component, enabled) {
    return {id, name, component, enabled};
}

const MENU_ITEM = [
    {title: 'Profile', icon: <GroupOutlinedIcon/>},
    {title: 'System Preferences', icon: <DisplaySettingsIcon/>},
    {title: 'Accessibility', icon: <AccessibilityIcon/>},
    {title: 'Devices', icon: <DesktopMacIcon/>},
];

const UserProperties = ({open, onClose}) => {
    const classes = useSettingsStyles();
    const {t} = useTranslation();
    const dispatch = useDispatch();

    const currentUsedTheme = window._getTheme();
    // Selectors
    const specialties = useSelector(specialtiesReducer);

    // User Settings
    const [settings, setSettings] = useState({});
    const [selectedTheme, setSelectedTheme] = useState(window._getTheme());

    // Specialty
    const [specialtiesArr, setSpecialtiesArr] = useState([]);
    const [activeSpecialty, setActiveSpecialty] = useState(null);
    const [selectedTab, setSelectedTab] = useState(MENU_ITEM[0].title);
    const [selectSubTab, setSelectSubTab] = useState(SYSTEM_PREFERENCES[0].id);

    // Reset
    const resetFunctions = useRef([]);
    const resetThemeFunction = useRef([]);
    const [isResetOpen, openReset, closeReset] = useOpenState(false, true);

    useEffect(() => {
        specialties.forEach((s) => {
            if ('isActive' in s)
                if (s.isActive)
                    setActiveSpecialty(s.__id);
        });
        setSpecialtiesArr(specialties);
    }, [specialties]);

    const handleResetState = () => {
        let reset = {};
        let resetTheme = '';
        try {
            _.forEach(resetFunctions.current, ref => {
                for (const [key, value] of Object.entries(ref)) {
                    if (value?.defaultState) {
                        reset = {
                            ...reset,
                            ...value.defaultState(),
                        };
                    }
                }
            });
            _.forEach(resetThemeFunction.current, ref => {
                for (const [key, value] of Object.entries(ref)) {
                    if (value?.defaultState) {
                        resetTheme = value.defaultState();
                    }
                }
            });
        } catch (e) {
            console.error('Error', e);
        }

        if (!_.isEmpty(reset)) {
            dispatch(saveAgylePreference(reset, false, false, true));
        }

        if (!_.isEmpty(resetTheme)) {
            setSelectedTheme(resetTheme);
            dispatch(saveCurrentTheme(resetTheme));
        }

        closeReset();
    };

    const handleSaveChanges = () => {
        let someSave = false;
        if (!_.isEqual(specialtiesArr, specialties)) {
            dispatch(saveSpecialties(specialtiesArr));
            someSave = true;
        }

        if (!_.isEmpty(settings)) {
            dispatch(saveAgylePreference(settings, false, false, true));
            someSave = true;
            setSettings({});
        }

        if (selectedTheme !== currentUsedTheme) {
            dispatch(saveCurrentTheme(selectedTheme, !!_.isEmpty(settings)));
            window._switchTheme();
            someSave = true;
        }

        if (!someSave) {
            dispatch(setSnackbar({type: 'info', message: 'No changes to save.'}));
        }
    };

    const handleTabChange = (item) => {
        resetFunctions.current = [];
        setSelectedTab(item.title);
    };

    const handleSubTabChange = (item) => {
        setSelectSubTab(item);
    };

    const handleAddSpec = useCallback((val) => {
        const clone = _.cloneDeep(specialtiesArr || []);
        clone.push(val);
        setSpecialtiesArr(clone);
    }, [specialtiesArr]);

    const handleDeleteSpec = useCallback((val) => {
        let clone = _.cloneDeep(specialtiesArr || []);
        clone = _.filter(clone, spec => spec?.__id !== val?.__id);
        setSpecialtiesArr(clone);
    }, [specialtiesArr]);

    const changeActiveSpec = useCallback((e, spec) => {
        const disableOpenPatient = e.target.closest('.disabled-options') !== null;
        if (!disableOpenPatient && spec?.__id && spec?.__id !== activeSpecialty) {
            const specArr = UserPropertiesUtils.buildActiveSpecialties(spec?.__id, specialtiesArr);
            setSpecialtiesArr(specArr);
            setActiveSpecialty(spec.__id);
        }
    }, [activeSpecialty, specialtiesArr]);

    const renderHeader = () => {
        return (
            <div className={classes.divRow} style={{gap: 10}}>
                <AgyleLogoIcon style={{fontSize: 24}}/>
                <span className={classes.title}>
                    {t('Agyle Settings')}
                </span>

                <div className={clsx(classes.divRow, classes.buttons)}>
                    <AgyleButton
                        size="small"
                        startIcon={<HistoryIcon/>}
                        colour="primary"
                        onClick={openReset}
                        disabled={selectedTab !== 'System Preferences' && selectedTab !== 'Accessibility'}
                    >
                        {t('Reset')}
                    </AgyleButton>

                    <AgyleButton
                        size="small"
                        variant="contained"
                        onClick={handleSaveChanges}
                    >
                        {t('Save Changes')}
                    </AgyleButton>

                    <IconButton
                        size="small"
                        onClick={onClose}
                    >
                        <CloseIcon/>
                    </IconButton>
                </div>
            </div>
        );
    };

    const handleChangeConfig = (type, value) => {
        let clone = _.cloneDeep(settings);
        clone[type] = value;

        setSettings(clone);
    };

    const handleChangeCurrentTheme = (value) => {
        let clone = _.clone(selectedTheme);
        if (value === clone) return;

        clone = value;
        setSelectedTheme(clone);
    };

    const renderSettings = () => {
        const item = _.find(SYSTEM_PREFERENCES, {id: selectSubTab});
        return (
            <>
                <UserSettingContent
                    title={item.name}
                >
                    {React.cloneElement(
                        item.component,
                        {
                            onChange: handleChangeConfig,
                            ref: (ref) => resetFunctions.current.push({[item.id]: ref}),
                        },
                    )}
                </UserSettingContent>
            </>
        );
    };

    const renderProfileSettings = () => {
        return (
            <>
                <UserSettingContent title={t('User Specialties')}>
                    <div>
                        <FormLabel style={{fontSize: 'small'}}>
                            {t('Add Specialty')}
                        </FormLabel>

                        <div style={{marginTop: 5}}>
                            <ControlSpecialty
                                color="tertiary"
                                label={t('Select user specialty')}
                                alreadySelected={specialtiesArr}
                                onChange={handleAddSpec}
                                noBorder
                                withBackground={false}
                                paperBackground
                            />
                        </div>
                    </div>

                    <div style={{marginTop: 10}}>
                        <SelectedUserSpecialties
                            selectedSpecialties={specialtiesArr}
                            onDeleteSpecialty={handleDeleteSpec}
                            onChangeActiveSpecialty={changeActiveSpec}
                        />
                    </div>
                </UserSettingContent>
            </>
        );
    };

    const renderAccessibility = () => {
        const id = uuidv4();

        return (
            <UserSettingContent
                key={id}
                title={t('Accessibility Settings')}
            >
                {React.cloneElement(
                    <DefaultTheme/>,
                    {
                        currentTheme: selectedTheme,
                        onChange: handleChangeCurrentTheme,
                        ref: (ref) => _.isEmpty(resetThemeFunction.current) && resetThemeFunction.current.push({'currentTheme': ref}),
                    },
                )}
            </UserSettingContent>
        );
    };


    const renderSettingTab = () => {
        switch (selectedTab) {
            case 'Profile':
                return renderProfileSettings();
            case 'System Preferences':
                return renderSettings();
            case 'Accessibility':
                return renderAccessibility();
            case 'Devices':
                return <DevicesTab/>;
            default:
                return 'no tab';
        }
    };

    const canSelectTab = (tab) => {
       if (tab.id === 'default-scheduler-view') {
           return tab.enabled && dispatch(hasPermission("AGYLE_CAN_ACCESS_SCHEDULER"));
       } else {
           return tab.enabled;
        }
    }

    return (
        <>
            <Dialog
                disableEnforceFocus
                fullScreen
                open={open}
                onClose={onClose}
            >
                <DialogContent className={classes.dialogContent}>
                    {renderHeader()}

                    <Divider className={classes.divider}/>

                    <div style={{display: 'flex'}}>
                        <div className={classes.menu}>
                            {selectedTab === 'System Preferences' &&
                                <>
                                    <div className={classes.backItem}>
                                        <IconButton
                                            onClick={() => handleTabChange({title: 'Profile'})}
                                            className={classes.backIcon}
                                        >
                                            <ArrowBackIcon/>
                                        </IconButton>

                                        {t('System Preferences')}
                                    </div>

                                    <List dense>
                                        {SYSTEM_PREFERENCES.map((item) => {
                                            const selectable = canSelectTab(item);
                                            if (!selectable) return null;

                                            return (
                                                <ListItem key={item.id} sx={{pl: 0, pr: 1}}>
                                                    <ListItemButton
                                                        className={clsx(classes.menuItem, item.id === selectSubTab && classes.menuItemSelected)}
                                                        onClick={() => handleSubTabChange(item.id)}
                                                    >
                                                        <Typography
                                                            variant="body1"
                                                            fontWeight={500}
                                                        >
                                                            {item.name}
                                                        </Typography>
                                                    </ListItemButton>
                                                </ListItem>
                                            );
                                        })}
                                    </List>
                                </>
                            }

                            {selectedTab !== 'System Preferences' &&
                                <List dense>
                                    {MENU_ITEM.map((item) => (
                                        <ListItem key={item.title} sx={{pl: 0, pr: 1}}>
                                            <ListItemButton
                                                className={clsx(classes.menuItem, item.title === selectedTab && classes.menuItemSelected)}
                                                onClick={() => handleTabChange(item)}
                                            >
                                                <div className={classes.divRow}
                                                     style={{color: item.title === selectedTab ? '#2180f7' : 'inherit'}}>
                                                    {item.icon}
                                                </div>

                                                <Typography
                                                    variant="body1"
                                                    fontWeight={500}
                                                >
                                                    {item.title}
                                                </Typography>
                                            </ListItemButton>
                                        </ListItem>
                                    ))}
                                </List>
                            }
                        </div>

                        <div className={clsx(classes.content, 'scroll-normal-white')}>
                            {renderSettingTab()}
                        </div>
                    </div>
                </DialogContent>
            </Dialog>

            <MessageBox
                onClose={closeReset}
                title={t('Confirmation')}
                haveAccept={{yes: t('Submit'), no: t('Close')}}
                onAccept={handleResetState}
                message={t('Are you sure you want to reset all preferences?')}
                open={isResetOpen}
            />
        </>
    );
};

export default UserProperties;

export class UserPropertiesUtils {
    static buildActiveSpecialties = (specialtyId, baseSpecialtiesArr) => {
        let specArr = [];
        baseSpecialtiesArr.forEach(spec => {
                if (spec.__id === specialtyId) {
                    specArr.push({...spec, isActive: true});
                } else {
                    let s = {...spec};
                    delete s.isActive;
                    specArr.push(s);
                }
            },
        );
        return specArr;
    };
}